import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Loader2, X, Check, MapPin, Users, ShieldCheck, Lock } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useDonationState } from './donation/useDonationState';
import { AmountPicker, ProviderPicker, DonorFields, MessageField, DonationSuccess, PROVIDERS, formatN } from './donation/parts';

const isDev = process.env.NODE_ENV === 'development';
const MIN_AMOUNT = 100;
const PRIVATE_MAX_AMOUNT = 250000;

function DonationSummary({ cagnotte, accent }) {
  const pct = Math.min(100, Math.round(((cagnotte.raised || 0) / (cagnotte.goal || 1)) * 100));
  return (
    <div className="mt-3">
      <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[12px] text-neutral-600">
        <span><span className="font-semibold text-[#1a1d1a]">{formatN(cagnotte.raised || 0)}</span> / {formatN(cagnotte.goal)} FCFA</span>
      </div>
    </div>
  );
}

export default function DonationModal({ open, onOpenChange, cagnotte, onDonated }) {
  const { state, setValue, markSuccess } = useDonationState(open);
  const [submitting, setSubmitting] = useState(false);
  const [cinetpayLoading, setCinetpayLoading] = useState(false);
  const [cinetpayCfg, setCinetpayCfg] = useState({ configured: false, mode: 'disabled' });
  const { toast } = useToast();
  const { user, requireAuth } = useAuth();
  const [consented, setConsented] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    if (open) setConsentChecked(false);
  }, [open]);

  useEffect(() => {
    if (!open || !state.isPrivate || !user) return undefined;
    let alive = true;
    api.get('/consent/don-prive')
      .then((r) => { if (alive) setConsented(Boolean(r.data.accepted)); })
      .catch(() => { if (alive) setConsented(false); });
    return () => { alive = false; };
  }, [open, state.isPrivate, user]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    api.get('/cinetpay/config')
      .then((cp) => { if (alive) setCinetpayCfg(cp.data); })
      .catch(() => { if (alive) setCinetpayCfg({ configured: false, mode: 'disabled' }); });
    return () => { alive = false; };
  }, [open]);

  const { amount, provider, donorName, donorPhone, message, anonymous, isPrivate, success } = state;

  const isYako = cagnotte?.type === 'Yako';
  const accent = isYako ? '#5a8268' : '#d97757';
  const accentBg = isYako ? 'bg-[#5a8268]' : 'bg-[#d97757]';
  const accentHover = isYako ? 'hover:bg-[#4a6f57]' : 'hover:bg-[#c66745]';
  const providerName = PROVIDERS.find((p) => p.id === provider)?.name || provider;

  const payWithCinetPay = async () => {
    if (!cagnotte?.id) return;
    const amt = Number(amount);
    if (amt < MIN_AMOUNT) {
      toast({ title: 'Montant trop bas', description: `Minimum ${MIN_AMOUNT} FCFA.` });
      return;
    }
    if (amt % 5 !== 0) {
      toast({ title: 'Montant invalide', description: 'CinetPay exige un montant multiple de 5 FCFA.' });
      return;
    }
    setCinetpayLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/paiement/retour`;
      const { data } = await api.post(`/cagnottes/${cagnotte.id}/donate/cinetpay/init`, {
        amount: amt,
        currency: 'XOF',
        channels: 'MOBILE_MONEY',
        donorName: anonymous ? undefined : (donorName || undefined),
        donorPhone: anonymous ? undefined : (donorPhone || undefined),
        message: message || undefined,
        anonymous,
        callbackUrl,
      });
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
        return;
      }
      throw new Error('Réponse CinetPay invalide');
    } catch (err) {
      if (isDev) console.warn('[cinetpay] init failed', err);
      const detail = err?.response?.data?.detail || err.message || 'Impossible de démarrer le paiement CinetPay.';
      toast({ title: 'CinetPay indisponible', description: detail });
    } finally { setCinetpayLoading(false); }
  };

  const submit = async () => {
    if (!cagnotte?.id) return;
    if (Number(amount) < MIN_AMOUNT) {
      toast({ title: 'Montant trop bas', description: `Minimum ${MIN_AMOUNT} FCFA.` });
      return;
    }
    if (isPrivate) {
      if (!requireAuth('don-prive')) return;
      if (Number(amount) > PRIVATE_MAX_AMOUNT) {
        toast({ title: 'Montant trop élevé', description: 'Un don privé est limité à 250 000 FCFA.' });
        return;
      }
      if (consented === false && !consentChecked) {
        toast({ title: 'Consentement requis', description: 'Veuillez accepter la clause CGU « Don Privé » avant de confirmer.' });
        return;
      }
    }
    setSubmitting(true);
    try {
      const url = isPrivate ? `/cagnottes/${cagnotte.id}/donate/prive` : `/cagnottes/${cagnotte.id}/donate`;
      const payload = isPrivate
        ? { amount: Number(amount), provider, message: message || undefined, consent: consentChecked }
        : {
          amount: Number(amount),
          provider,
          donorName: anonymous ? undefined : (donorName || undefined),
          donorPhone: anonymous ? undefined : (donorPhone || undefined),
          message: message || undefined,
          anonymous,
        };
      const { data } = await api.post(url, payload);
      if (isPrivate) setConsented(true);
      markSuccess();
      if (onDonated) onDonated(data.cagnotte);
      toast({
        title: isPrivate ? 'Don privé envoyé 🔒' : 'Merci pour votre don ❤\ufe0f',
        description: isPrivate
          ? `${formatN(amount)} FCFA envoyés en privé (paiement simulé). Il n'apparaîtra pas publiquement.`
          : `${formatN(amount)} FCFA envoyés (paiement simulé).`,
      });
    } catch (err) {
      if (isDev) console.warn('[donation] échec de la donation', err);
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible d\u2019enregistrer le don.' });
    } finally { setSubmitting(false); }
  };

  if (!cagnotte) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-black/5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase font-semibold" style={{ color: accent }}>Faire un don · {cagnotte.type}</div>
              <DialogTitle asChild>
                <h3 className="mt-1 text-[18px] font-semibold text-[#1a1d1a] leading-tight">{cagnotte.title}</h3>
              </DialogTitle>
              <DialogDescription className="sr-only">Formulaire de don pour la cagnotte {cagnotte.title}</DialogDescription>
              <div className="mt-1 flex items-center gap-3 text-[12px] text-neutral-500">
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {cagnotte.country}</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {cagnotte.donors || 0} donateurs</span>
              </div>
            </div>
            <button onClick={() => onOpenChange(false)} className="p-2 rounded-full hover:bg-black/5" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
          <DonationSummary cagnotte={cagnotte} accent={accent} />
        </div>

        {!success ? (
          <div className="px-6 py-5 overflow-y-auto space-y-5">
            <AmountPicker amount={amount} setAmount={(v) => setValue('amount', v)} accent={accent} />

            <button
              type="button"
              data-testid="private-donation-toggle"
              onClick={() => {
                if (!isPrivate && !user) { requireAuth('don-prive'); return; }
                setValue('isPrivate', !isPrivate);
              }}
              className={`w-full text-left rounded-2xl border p-4 transition-colors ${isPrivate ? 'border-[#1a1d1a] bg-[#1a1d1a]/[0.04]' : 'border-black/10 hover:border-black/25 bg-white'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isPrivate ? 'bg-[#1a1d1a] text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                  <Lock className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[14px] font-semibold text-[#1a1d1a]">Don privé</span>
                    <span data-testid="private-donation-status" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${isPrivate ? 'bg-[#1a1d1a] text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                      {isPrivate ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                  <p className="mt-1 text-[11.5px] text-neutral-600 leading-relaxed">
                    Votre don n'apparaîtra pas dans la liste publique des donateurs. Seuls l'organisateur et l'administration peuvent le voir (traçabilité conservée).
                  </p>
                  <p className="mt-1 text-[11px] font-medium text-neutral-500">Max 250 000 FCFA · 2 dons privés max par cagnotte · Connexion requise</p>
                </div>
              </div>
            </button>
            <div className="!mt-1.5 text-right">
              <a href="/cgu#don-prive" target="_blank" rel="noreferrer" data-testid="private-donation-cgu-link" className="text-[11px] text-neutral-500 underline hover:text-[#1a1d1a]">
                Voir la clause CGU « Don Privé » →
              </a>
            </div>

            {isPrivate && consented === false && (
              <label data-testid="private-consent-block" className="!mt-2 flex items-start gap-2.5 rounded-2xl border border-amber-300 bg-amber-50 p-3.5 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="private-consent-checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 accent-[#1a1d1a]"
                />
                <span className="text-[12px] text-amber-900 leading-relaxed">
                  J'ai lu et j'accepte la <a href="/cgu#don-prive" target="_blank" rel="noreferrer" className="underline font-semibold">clause CGU « Don Privé »</a>. Je comprends que mon don est masqué publiquement mais reste entièrement enregistré (identité, montant, date) et peut être communiqué aux autorités compétentes en cas d'obligation légale.
                </span>
              </label>
            )}

            <ProviderPicker provider={provider} setProvider={(v) => setValue('provider', v)} />
            {!anonymous && !isPrivate && (
              <DonorFields
                donorName={donorName} setDonorName={(v) => setValue('donorName', v)}
                donorPhone={donorPhone} setDonorPhone={(v) => setValue('donorPhone', v)}
              />
            )}
            <MessageField
              isYako={isYako}
              message={message} setMessage={(v) => setValue('message', v)}
              anonymous={anonymous} setAnonymous={(v) => setValue('anonymous', v)}
              hideAnonymous={isPrivate}
            />

            {!isPrivate && cinetpayCfg.configured && (
              <div data-testid="cinetpay-block" className="rounded-2xl border-2 p-4 transition-all" style={{ background: `${accent}0d`, borderColor: accent }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm" style={{ color: accent }}>
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[14px] font-semibold text-[#1a1d1a]">Mobile Money CinetPay</div>
                      <span data-testid="recommended-pay-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1a1d1a] text-white">★ Recommandé</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Sécurisé</span>
                    </div>
                    <div className="mt-0.5 text-[12.5px] text-neutral-600">Wave · Orange Money · MTN MoMo · Moov Money · Carte bancaire</div>
                    <Button data-testid="pay-cinetpay-btn" onClick={payWithCinetPay} disabled={cinetpayLoading} className="btn-anim mt-3 w-full h-10 rounded-full text-white text-[13.5px] font-medium" style={{ background: accent }}>
                      {cinetpayLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                      Payer {formatN(amount)} FCFA via Mobile Money
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="text-[11.5px] text-neutral-500 bg-neutral-50 rounded-lg p-3 border border-black/5">
              {cinetpayCfg.configured
                ? <>Le bouton <span className="font-medium">CinetPay</span> traite des paiements réels (Wave, Orange, MTN, Moov). Le bouton <span className="font-medium">Confirmer</span> reste en mode simulé (aucun débit).</>
                : <>Paiement simulé (aucun débit réel). CinetPay sera activé dès réception des clés API.</>
              }
            </div>
          </div>
        ) : (
          <DonationSuccess amount={amount} providerName={providerName} accent={accent} cagnotte={cagnotte} />
        )}

        <div className="px-6 py-4 border-t border-black/5 bg-white flex items-center justify-end gap-2">
          {!success ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="btn-anim h-10 rounded-full px-4 text-neutral-700">Annuler</Button>
              <Button data-testid="donation-confirm-btn" onClick={submit} disabled={submitting || (isPrivate && consented === false && !consentChecked)} className={`btn-anim h-11 rounded-full px-5 text-white ${isPrivate ? 'bg-[#1a1d1a] hover:bg-black' : `${accentBg} ${accentHover}`}`}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (isPrivate ? <Lock className="w-4 h-4 mr-2" /> : <Check className="w-4 h-4 mr-2" />)}
                {isPrivate ? 'Don privé' : 'Confirmer'} {formatN(amount)} FCFA
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="btn-anim h-11 rounded-full px-5 bg-[#1a1d1a] hover:bg-black text-white">Fermer</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
